import { Component, effect, inject, input, OnInit, signal } from '@angular/core';
import { Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { IUserGroup } from '@core/interfaces/user-group.interface';
import { TableStateService } from '@core/services/table-state.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DatatableBaseComponent } from '@shared/components/datatable-base/datatable-base.component';
import { DatatableComponent } from '@shared/components/datatable/datatable.component';
import {
  IDataRefreshRequestParams,
  IDatatableConfiguration,
  TDatatableColumn,
} from '@shared/components/datatable/datatable.interface';
import { IconBadgeComponent } from '@shared/components/icon-badge/icon-badge.component';
import { ISkeletonLoaderConfig } from '@shared/components/skeleton-loader/skeleton-loader.interface';
import { combineLatest, distinctUntilChanged, filter } from 'rxjs';
import { IAdminUser } from '../admin-user-group.interface';
import { AdminUsersTabStore } from './admin-users-tab.store';

@Component({
  imports: [DatatableComponent, TranslateModule, IconBadgeComponent],
  providers: [AdminUsersTabStore],
  selector: 'app-admin-users-tab',
  styleUrl: './admin-users-tab.component.scss',
  templateUrl: './admin-users-tab.component.html',
})
export class AdminUsersTabComponent extends DatatableBaseComponent implements OnInit {
  public readonly userGroups = input.required<IUserGroup[] | null>();
  public readonly dataSource = signal<MatTableDataSource<IAdminUser>>(new MatTableDataSource<IAdminUser>([]));
  public readonly loadedTableData = signal<boolean>(false);
  public readonly store = inject(AdminUsersTabStore);

  public datatableColumns: TDatatableColumn<IAdminUser, string>[] = [
    { id: 'iconId' },
    { id: 'name' },
    { id: 'userId' },
    { id: 'userGroupId' },
    { id: 'firebaseUid' },
  ];

  public datatableConfig!: IDatatableConfiguration<IAdminUser>;

  public readonly skeletonLoaderConfig: ISkeletonLoaderConfig = {
    columns: [
      {
        borderRadius: '100px',
        circleSize: '40px',
        className: 'w-[6%]',
        columnPadding: '0 8px',
        rowPadding: '8px 0',
        type: 'icon',
        width: '40px',
      },
      { borderRadius: '4px', className: 'w-[13%]', textWidth: '80px', type: 'text' },
      { borderRadius: '4px', className: 'w-[28%]', textWidth: '120px', type: 'text' },
      { borderRadius: '4px', className: 'w-[28%]', textWidth: '120px', type: 'text' },
      { borderRadius: '4px', className: 'w-[25%]', textWidth: '120px', type: 'text' },
    ],
    filters: [{ inputClass: '!min-w-[220px]' }],
    rows: 5,
    showFilters: true,
    showHeaderControls: true,
    showSearchField: true,
  };

  private readonly translate = inject(TranslateService);
  private readonly tableStateService: TableStateService = inject(TableStateService);
  private readonly tableStorageKey: string = this.tableStateService.buildKey('admin-user-group', 'users-tab');

  constructor() {
    super();

    effect(() => {
      const data = this.store.data$();

      if (data && (data.length > 0 || !this.store.dataLoading$())) {
        const mappedData = data.map((user: IAdminUser) => ({
          ...user,
          // userGroupName: this.userGroups().find((ug) => ug.id === user.userGroupId)?.name,
          userId: user.id,
        }));
        this.dataSource().data = mappedData;
        this.loadedTableData.set(true);
      }
    });

    effect(() => {
      this.userGroups();

      this.setDatatableConfig();
    });
  }

  public ngOnInit(): void {
    this.setDatatableConfig();

    this.subscriptions.push(
      combineLatest({
        datatable: this.datatableParams$.pipe(filter(Boolean)),
      })
        .pipe(distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)))
        .subscribe(({ datatable }) => {
          this.store.fetchUsers(this.prepareFetchParams(datatable));
        }),
    );
  }

  private prepareFetchParams(params: IDataRefreshRequestParams): {
    filters?: Record<string, unknown>;
    joins?: string[];
    limit: number;
    page: number;
    search?: { searchedFields: string[]; searchText: string };
    sort: Sort[];
  } {
    return {
      limit: params.limit,
      page: params.page,
      sort: [params.sort?.active === 'userId' ? { ...params.sort, active: 'id' } : params.sort],
      ...(params.search
        ? { search: { searchedFields: ['name__ilike', 'firebaseUid__ilike'], searchText: params.search } }
        : {}),
      ...(params.filters && Object.keys(params.filters).length ? { filters: params.filters } : {}),
    };
  }

  private setDatatableConfig(): void {
    this.datatableConfig = {
      filters: [
        {
          key: 'user_group_id__in',
          label: this.translate.instant('pages.admin.userGroup.userGroup'),
          multiple: true,
          options:
            this.userGroups()?.map((ug) => ({
              label: ug.name,
              value: ug.id,
            })) ?? [],
          placeholder: `${this.translate.instant('shared.all')}`,
        },
      ],
      isClientSide: false,
      isRowClickEnabled: false,
      skipInitialRequest: false,
      storageKey: this.tableStorageKey,
    } satisfies IDatatableConfiguration<IAdminUser>;
  }
}
